import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  Collapse,
  Button,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

const SimpleObjectSelector = ({ 
  objekte = [], 
  selectedObjekt = null, 
  onObjektSelect, 
  disabled = false,
  placeholder = "Objekt auswählen..." 
}) => {
  const [searchMode, setSearchMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  // 🔍 Objekte filtern basierend auf Suchbegriff
  const filterObjekte = (objekte, searchTerm) => {
    if (!searchTerm) return objekte;
    
    return objekte.filter(objekt => {
      const searchText = searchTerm.toLowerCase();
      return (
        (objekt.objektname || '').toLowerCase().includes(searchText) ||
        (objekt.objektadresse || '').toLowerCase().includes(searchText) ||
        (objekt.objekttyp || '').toLowerCase().includes(searchText)
      );
    });
  };

  // 🎨 Objekttyp-Icon
  const getObjektIcon = (objekttyp) => {
    switch (objekttyp?.toLowerCase()) {
      case 'wohnung':
      case 'apartment':
        return <HomeIcon sx={{ color: '#1976d2' }} />;
      case 'gewerbe':
      case 'büro':
      case 'laden':
        return <BusinessIcon sx={{ color: '#ed6c02' }} />;
      default:
        return <HomeIcon sx={{ color: '#757575' }} />;
    }
  };

  // 🎨 Objekttyp-Farbe
  const getObjektColor = (objekttyp) => {
    switch (objekttyp?.toLowerCase()) {
      case 'wohnung':
      case 'apartment':
        return '#1976d2';
      case 'gewerbe':
      case 'büro':
      case 'laden':
        return '#ed6c02';
      default:
        return '#757575';
    }
  };

  // 📝 Objekt-Anzeigetext
  const getObjektDisplayText = (objekt) => {
    if (!objekt) return placeholder;
    return `${objekt.objektname || 'Unbenannt'} - ${objekt.objektadresse || 'Keine Adresse'}`;
  };

  // 🔄 Modus zwischen Dropdown und Search umschalten
  const shouldUseSearchMode = objekte.length > 10;

  const filteredObjekte = filterObjekte(objekte, searchTerm);

  const handleObjektSelect = (objekt) => {
    onObjektSelect(objekt);
    setIsExpanded(false);
    setSearchTerm('');
    setSearchMode(false);
  };

  const handleClearSelection = () => {
    onObjektSelect(null);
    setSearchTerm('');
  };

  const toggleExpanded = () => {
    if (disabled) return;
    if (shouldUseSearchMode && !searchMode) {
      setSearchMode(true);
      setIsExpanded(true);
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <Box>
      {/* Hauptauswahlfeld */}
      <Card 
        sx={{ 
          cursor: disabled ? 'default' : 'pointer',
          backgroundColor: disabled ? '#f5f5f5' : '#fff',
          '&:hover': disabled ? {} : { 
            boxShadow: 2,
            borderColor: '#1976d2' 
          },
          border: '1px solid #e0e0e0',
          transition: 'all 0.2s ease'
        }}
        onClick={toggleExpanded}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              {selectedObjekt && getObjektIcon(selectedObjekt.objekttyp)}
              
              <Box sx={{ flex: 1 }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: selectedObjekt ? 'text.primary' : 'text.secondary',
                    fontWeight: selectedObjekt ? 500 : 400
                  }}
                >
                  {getObjektDisplayText(selectedObjekt)}
                </Typography>
                
                {selectedObjekt && (
                  <Chip 
                    label={selectedObjekt.objekttyp || 'Unbekannt'}
                    size="small"
                    sx={{ 
                      mt: 0.5,
                      backgroundColor: `${getObjektColor(selectedObjekt.objekttyp)}20`,
                      color: getObjektColor(selectedObjekt.objekttyp),
                      fontWeight: 500
                    }}
                  />
                )}
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {selectedObjekt && (
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearSelection();
                  }}
                  sx={{ minWidth: 'auto', p: 0.5 }}
                >
                  <ClearIcon fontSize="small" />
                </Button>
              )}
              
              {!disabled && (
                shouldUseSearchMode && !searchMode ? (
                  <SearchIcon sx={{ color: '#666' }} />
                ) : (
                  isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                )
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Erweiterte Ansicht */}
      <Collapse in={isExpanded}>
        <Card sx={{ mt: 1, border: '1px solid #e0e0e0' }}>
          <CardContent sx={{ p: 2 }}>
            {/* Suchfeld für viele Objekte */}
            {shouldUseSearchMode && (
              <TextField
                fullWidth
                placeholder="Objekt suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 2 }}
                autoFocus
              />
            )}
            
            {/* Objekte Liste */}
            <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
              {filteredObjekte.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                  {searchTerm ? 'Keine passenden Objekte gefunden' : 'Keine Objekte verfügbar'}
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {filteredObjekte.map((objekt) => (
                    <Card
                      key={objekt.objektid}
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        backgroundColor: selectedObjekt?.objektid === objekt.objektid ? '#e3f2fd' : '#fff',
                        '&:hover': { 
                          backgroundColor: selectedObjekt?.objektid === objekt.objektid ? '#e3f2fd' : '#f5f5f5',
                          transform: 'translateY(-1px)',
                          boxShadow: 2
                        }
                      }}
                      onClick={() => handleObjektSelect(objekt)}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {getObjektIcon(objekt.objekttyp)}
                          
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {objekt.objektname || 'Unbenannt'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {objekt.objektadresse || 'Keine Adresse'}
                            </Typography>
                          </Box>
                          
                          <Chip 
                            label={objekt.objekttyp || 'Unbekannt'}
                            size="small"
                            sx={{ 
                              backgroundColor: `${getObjektColor(objekt.objekttyp)}20`,
                              color: getObjektColor(objekt.objekttyp),
                              fontWeight: 500
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>
          </CardContent>
        </Card>
      </Collapse>
    </Box>
  );
};

export default SimpleObjectSelector;
